import { useEffect, useRef, useState } from 'react';
import { DURATION, reducedMotion } from './motion';

/**
 * A number that changes counts briefly to its new value, so a reader sees that
 * it changed and by how much. Request of 5 October 2026, section 6. The first
 * value shows as it is; only changes count, over DURATION.slow, easing out.
 * Off under reduced motion, where the new value simply replaces the old.
 *
 * Not for a running timer, which ticks on its own.
 */
export function useCountUp(value: number | undefined): number | undefined {
  const [shown, setShown] = useState(value);
  const from = useRef(value);

  useEffect(() => {
    const start = from.current;
    from.current = value;
    if (value === undefined || start === undefined || start === value || reducedMotion()) {
      setShown(value);
      return;
    }
    if (typeof requestAnimationFrame !== 'function') {
      setShown(value);
      return;
    }
    const began = performance.now();
    let frame = 0;
    const step = (now: number) => {
      const t = Math.min(1, (now - began) / DURATION.slow);
      const eased = 1 - (1 - t) ** 3;
      setShown(Math.round(start + (value - start) * eased));
      if (t < 1) frame = requestAnimationFrame(step);
    };
    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [value]);

  return shown;
}
